/**
 * @fileoverview V2 Step Completion — Re-implementation of V1's useStepCompletion
 * without dependency on UnifiedFlowContext. Uses audit record query directly.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import { useGetBlobsQuery } from '@/services/api/blobService';
import { useGetCaseIngestionQuery } from '@/services/api/caseIngestionService';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import { useGetIncidentsQuery } from '@/services/api/incidentService';
import {
  useGetMatterCompanionsQuery,
  useGetMatterDetailsQuery,
} from '@/services/api/mattersService';
import { usePoliceReportsListQuery } from '@/services/api/policeReportsService';
import { useGetStaffNotesQuery } from '@/services/api/staffNotesService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';
import { CoverageCategory } from '@/services/types/coverage-types';
import type { MatterClient } from '@/services/types/matter-client-types';

import {
  extractNoteContent,
  GENERAL_NOTES_HEADER,
  INTAKE_NOTES_HEADER,
} from '@/components/case-ingestion/utils/notesParser';

export interface StepCompletionStatus {
  basics: boolean;
  case_info: boolean;
  client_info: boolean;
  companions: boolean;
  liability: boolean;
  treatment: boolean;
  coverage: boolean;
  documents: boolean;
  notes: boolean;
  isLoading: boolean;
}

function isClientComplete(client: MatterClient | undefined): boolean {
  if (!client) return false;
  return !!(
    client.first_name?.trim() &&
    client.last_name?.trim() &&
    client.email?.trim() &&
    client.phone_numbers?.length &&
    client.date_of_birth
  );
}

export function useStepCompletion(matterId: string | null): StepCompletionStatus {
  const { data: auditRecord } = useGetCaseIngestionQuery(matterId ?? skipToken);
  const sections = auditRecord?.sections;

  const { data: matter, isLoading: l1 } = useGetMatterDetailsQuery(matterId ?? skipToken);
  const { data: incidents, isLoading: l2 } = useGetIncidentsQuery(matterId ? { matterId } : skipToken);
  const { data: policeReports, isLoading: l3 } = usePoliceReportsListQuery(matterId ? { matterId } : skipToken);
  const { data: providers, isLoading: l4 } = useGetMedicalProvidersQuery(matterId ?? skipToken);
  const { data: coverages, isLoading: l5 } = useGetCoveragesQuery(matterId ? { matterId } : skipToken);
  const { data: blobs, isLoading: l6 } = useGetBlobsQuery(matterId ?? skipToken);
  const { data: staffNotes, isLoading: l7 } = useGetStaffNotesQuery(matterId ? { matterId } : skipToken);
  const { data: companions, isLoading: l8 } = useGetMatterCompanionsQuery(matterId ?? skipToken);

  const isLoading = l1 || l2 || l3 || l4 || l5 || l6 || l7 || l8;

  return useMemo(() => {
    const basics = !!matter;
    const case_info = !!(matter?.date_of_loss && matter?.statute_of_limitations_date && incidents?.[0]?.location);
    const client_info = isClientComplete(matter?.clients?.[0]);

    const isPrimary = companions?.members.some((m) => m.id === matterId && m.is_primary) ?? false;
    const isGroupMember = (companions?.members.length ?? 0) > 1;
    const companionsDone = isPrimary || isGroupMember || (sections?.companion_cases ?? null) !== null;

    const liability = (policeReports?.length ?? 0) > 0 || (sections?.police_report ?? null) !== null;
    const treatment = (providers?.length ?? 0) > 0 || (sections?.medical_providers ?? null) !== null;

    const has1P = coverages?.some((c) => c.category === CoverageCategory.FIRST_PARTY) ?? false;
    const has3P = coverages?.some((c) => c.category === CoverageCategory.THIRD_PARTY) ?? false;
    const hasHealth = coverages?.some((c) => c.category === CoverageCategory.FIRST_PARTY_HEALTH) ?? false;
    const coverage =
      (has1P || (sections?.first_party_coverage ?? null) !== null) &&
      (has3P || (sections?.third_party_coverage ?? null) !== null) &&
      (hasHealth || (sections?.health_insurance ?? null) !== null);

    const documents = (blobs?.length ?? 0) > 0;

    const intakeNote = staffNotes?.find((n) => n.content.startsWith(INTAKE_NOTES_HEADER));
    const generalNote = staffNotes?.find((n) => n.content.startsWith(GENERAL_NOTES_HEADER));
    const notes =
      !!extractNoteContent(intakeNote?.content || '', INTAKE_NOTES_HEADER).trim() ||
      !!extractNoteContent(generalNote?.content || '', GENERAL_NOTES_HEADER).trim();

    return {
      basics,
      case_info,
      client_info,
      companions: companionsDone,
      liability,
      treatment,
      coverage,
      documents,
      notes,
      isLoading,
    };
  }, [matter, incidents, policeReports, providers, coverages, blobs, staffNotes, companions, matterId, sections, isLoading]);
}
